import { useState } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";

const companies = [
    { handle: "kalaanirealtors", name: "Kalaani Realtors", tag: "Real Estate Company", desc: "All-in-one: Buy, Sell, Furnish, Manage, Rent." },
    { handle: "kalaanidesign", name: "Kalaani Design", tag: "Architectural Designer", desc: "Tailored Havens." },
    { handle: "hharealestate", name: "HHA Real Estate", tag: "Real Estate", desc: "Home &middot; Happiness &middot; Assurance" },
    { handle: "skymax_property_dubai", name: "Sky Max Property", tag: "Real Estate Company", desc: "Elevate your lifestyle with luxury properties. Dubai's finest." },
    { handle: "prowess.realestate", name: "Prowess Real Estate", tag: "Real Estate", desc: "Precision-led property advisory." },
    { handle: "goldenbricks.ae", name: "Golden Bricks", tag: "Real Estate Company", desc: "Building futures, one brick at a time. Dubai." },
    { handle: "metrikaestate", name: "Metrika Estate", tag: "Real Estate Company", desc: "Data-driven property expertise across Dubai." },
];

const personal = [
    { handle: "ghizlanek1", name: "Ghizlane Karaoui", tag: "Founder & CEO", desc: "@ghizlane_real_estate &middot; Master in Behavioral Psychology." },
    { handle: "rolandkalaany", name: "Roland Al Kalaany", tag: "Investor / Entrepreneur", desc: "CEO — @kalaanirealtors &middot; @kalaanidesign" },
    { handle: "francis.dubairealtor", name: "Francis El Khazen", tag: "Sales Director", desc: "Dubai &middot; Abu Dhabi &middot; RAK &middot; Arabic / English / French" },
    { handle: "steve.dubairealtor", name: "Steve El Hitti", tag: "Real Estate Agent", desc: "Expert Property Consultant &middot; Dubai Real Estate Advisor" },
    { handle: "moubarak.charbel", name: "Moubarak Charbel", tag: "Property Consultant", desc: "Dubai real estate specialist." },
    { handle: "ramyseaada.dxb", name: "Ramy Seada", tag: "Real Estate Agent", desc: "Dubai off-plan &amp; secondary market." },
    { handle: "moustafa_hemidadxb", name: "Moustafa Hemida", tag: "Property Consultant", desc: "Dubai luxury real estate advisor." },
];

function ClientCard({ item }) {
    return (
        <a
            href={`https://www.instagram.com/${item.handle}/`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`client-card-${item.handle}`}
            className="group relative block bg-[#141414] border border-white/10 hover:border-brand/60 transition-colors p-6 sm:p-7"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand flex items-center justify-center font-display font-semibold text-white text-lg shrink-0">
                        {item.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                    </div>
                    <div>
                        <div className="font-display font-semibold text-lg text-white leading-tight tracking-[-0.02em]">{item.name}</div>
                        <div className="text-xs text-white/50 mt-0.5 font-mono">@{item.handle}</div>
                    </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
            </div>

            <div className="mt-5 pt-5 border-t border-white/10">
                <div className="text-[10px] font-mono uppercase tracking-widest text-brand">{item.tag}</div>
                <div
                    className="mt-2 text-sm text-white/70 leading-snug"
                    dangerouslySetInnerHTML={{ __html: item.desc }}
                />
            </div>
        </a>
    );
}

export default function HappyClients() {
    const [tab, setTab] = useState("companies");
    const data = tab === "companies" ? companies : personal;

    return (
        <section id="clients" className="relative py-24 sm:py-32 border-t border-white/10 overflow-hidden">
            <div className="pointer-events-none absolute -bottom-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-brand/10 blur-[180px]" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
                    <div className="md:col-span-8">
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> Some of our happy clients
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            Realtors we've<br />
                            <span className="text-brand">put on the map.</span>
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <a
                            href="https://instagram.com/growwithbabur"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-white/60 hover:text-brand transition-colors border-b border-white/20 hover:border-brand pb-1"
                        >
                            <Instagram className="w-4 h-4" /> Follow @growwithbabur &rarr;
                        </a>
                    </div>
                </div>

                {/* Tabs */}
                <div className="inline-flex border border-white/15 mb-10">
                    <button
                        data-testid="clients-tab-companies"
                        onClick={() => setTab("companies")}
                        className={`px-6 py-3 text-xs font-mono uppercase tracking-widest transition-colors ${
                            tab === "companies" ? "bg-brand text-white" : "text-white/60 hover:text-white"
                        }`}
                    >
                        Companies
                    </button>
                    <button
                        data-testid="clients-tab-personal"
                        onClick={() => setTab("personal")}
                        className={`px-6 py-3 text-xs font-mono uppercase tracking-widest transition-colors ${
                            tab === "personal" ? "bg-brand text-white" : "text-white/60 hover:text-white"
                        }`}
                    >
                        Personal Branding
                    </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {data.map((item) => (
                        <ClientCard key={item.handle} item={item} />
                    ))}
                </div>
            </div>
        </section>
    );
}
